<script setup lang="ts">
import { ref, watch } from 'vue';
import InputGroupAddon from "primevue/inputgroupaddon";
import InputGroup from "primevue/inputgroup";
import backendCall from "../../services/AxiosService";
import { useToast } from "primevue/usetoast";
import { generateClipInstallPdf } from "@/utilities/pdf/clipPdfService";
import logger from "../../utilities/logger";
import inventoryService, { type InventoryItemLocationWithDetails, type InventoryLocation as ServiceInventoryLocation } from "../../services/inventory";
import { useDebounceSubmit } from '@/composables/useDebounceSubmit';

const props = defineProps<{
  clipNumber: string;
  eventId: number;
  fuelLevel: string;
}>();
const emit = defineEmits(['updateClips']);
const installClipDialogVisible = defineModel<boolean>();
const toast = useToast();
const showPrintOption = ref(false);
const createdEventId = ref(null);
const lowFuelWarning = ref(false);


interface InstallFormObject {
  eventId: number | null;
  clipNumber: string | null;
  container: string | null;
  fuelLevel: string | null;
  setpoint: string | null;
  return: string | null;
  supply: string | null;
  notes: string | null;
  forkliftPropane: string | null;
  forkliftGas: string | null;
  forkliftHours: string | null;
}

interface InventoryItemSearchResult {
  id: number;
  name: string;
  description?: string | null;
  category_id?: number | null;
  sku?: string | null;
  part_number?: string | null;
  unit_of_measure: string;
  minimum_quantity?: number;
  current_quantity?: number;
  reorder_point?: number;
  reorder_quantity?: number;
  location?: string | null;
  location_id?: number | null;
  supplier_id?: number | null;
  cost_per_unit?: number | null;
  notes?: string | null;
  created_at?: Date;
  updated_at?: Date;
}

const installFormObject = ref<InstallFormObject>({
  eventId: props.eventId,
  clipNumber: props.clipNumber,
  container: null,
  fuelLevel: null,
  setpoint: null,
  return: null,
  supply: null,
  notes: null,
  forkliftPropane: null,
  forkliftGas: null,
  forkliftHours: null,
});

const fuelOptions = ref(['E', '1/8', '1/4', '3/8', '5/16', '7/16', '1/2', '9/16', '5/8', '11/16', '3/4', '13/16', '7/8', 'F']);

// Inventory-related reactive variables
const inventoryDialog = ref(false);
const inventoryItems = ref<ClipOnUsagePart[]>([]);
const selectedItem = ref<InventoryItemSearchResult | null>(null);
const itemQuantity = ref(1);
const itemNotes = ref('');
const selectedEquipmentType = ref('clip');
const itemSuggestions = ref<InventoryItemSearchResult[]>([]);
const isSearchingItems = ref(false);
const selectedLocation = ref<InventoryItemLocationWithDetails | null>(null);
const availableLocations = ref<ServiceInventoryLocation[]>([]);
const itemLocations = ref<InventoryItemLocationWithDetails[]>([]);
const isLoadingLocations = ref(false);

// Equipment type options
const equipmentTypeOptions = ref([
  { label: 'Clip Equipment', value: 'clip', icon: 'pi pi-bolt' },
  { label: 'Container Equipment', value: 'container', icon: 'pi pi-box' }
]);

const submitClipInstallLogic = async () => {

  if (!installFormObject?.value?.container) {
    return
  }

  // Validate required forklift fuel data
  if (!installFormObject.value.forkliftPropane ||
      !installFormObject.value.forkliftGas ||
      installFormObject.value.forkliftHours === null ||
      installFormObject.value.forkliftHours === undefined ||
      installFormObject.value.forkliftHours === '') {
    toast.add({
      severity: 'error',
      summary: 'Missing Forklift Data',
      detail: 'Forklift propane level, gas level, and engine hours are required',
      life: 5000
    });
    return;
  }

  // Validate container format - checking if 4th character is 'U'
  const normalizedContainer = installFormObject.value.container.trim().toUpperCase().replace(/\s/g, '');
  if (normalizedContainer.length > 3 && normalizedContainer[3] !== 'U') {
    toast.add({
      severity: 'error',
      summary: 'Invalid Container',
      detail: 'Please enter a valid container number',
      life: 3000
    });
    return;
  }

  // Check if fuel level is below 7/8
  const lowFuelLevels = ['E', '1/8', '1/4', '3/8', '5/16', '7/16', '1/2', '9/16', '5/8', '11/16', '3/4', '13/16'];

  if (props.fuelLevel && lowFuelLevels.includes(props.fuelLevel)) {
    // Show warning message
    toast.add({
      severity: 'warn',
      summary: 'Low Fuel Warning',
      detail: `This clip-on has a fuel level of ${props.fuelLevel}, which is below 7/8. Please refuel before installing.`,
      life: 5000
    });
    throw new Error('Low fuel level'); // Prevent installation
  }

  // Prepare the payload with inventory items
  const payload = {
    ...installFormObject.value,
    inventoryItems: inventoryItems.value
  };

  const clipInstallCall = await backendCall.post('clip-ons/install', payload);
  if (!clipInstallCall) {
    throw new Error('Failed to Install Clip')
  }

  // Store the event ID for later use with PDF generation
  if (clipInstallCall.data && clipInstallCall.data.data && clipInstallCall.data.data.eventId) {
    createdEventId.value = clipInstallCall.data.data.eventId;
    showPrintOption.value = true;
  }

  installClipDialogVisible.value = false;
  emit('updateClips');

  // Show success message with print option
  toast.add({
    severity: 'success',
    summary: 'Success!',
    detail: `${installFormObject.value.clipNumber} has been installed on ${installFormObject.value.container}`,
    life: 3000
  });

  // If we have an event ID, show a second toast with print option
  if (createdEventId.value) {
    toast.add({
      severity: 'info',
      summary: 'Print Ticket',
      detail: 'A PDF ticket is available for this installation',
      life: 5000,
      closable: true
      // Removed icon property as it's not supported by PrimeVue Toast
      // Removed onclick as it's not a standard toast property
    });
  }
};

// Use the debounced submit composable
const { isSubmitting, submit: submitClipInstall } = useDebounceSubmit(submitClipInstallLogic, {
  submissionKey: `clip-install-${props.clipNumber}`,
  trackDuplicates: true,
  errorMessage: 'An error occurred while trying to install the clip-on.'
});

const printInstallTicket = async () => {
  if (!createdEventId.value) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'No event ID available',
      life: 3000
    });
    return;
  }

  try {
    await generateClipInstallPdf(createdEventId.value);

    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Clip installation ticket generated successfully',
      life: 3000
    });
  } catch (error) {
    // Use logger instead of console.error for production-safe logging
    logger.error('Error generating clip installation ticket:', error);
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to generate clip installation ticket',
      life: 3000
    });
  }
};

// Inventory functions
const searchInventoryItems = async (event: { query: string }) => {
  if (!event.query || event.query.length < 2) {
    itemSuggestions.value = [];
    return;
  }

  try {
    isSearchingItems.value = true;
    const response = await inventoryService.searchInventoryItems(event.query);
    
    if (response.success && Array.isArray(response.data)) {
      itemSuggestions.value = response.data;
    } else {
      itemSuggestions.value = [];
    }
  } catch (error) {
    logger.error('Error searching inventory items:', error);
    itemSuggestions.value = [];
    toast.add({
      severity: 'error',
      summary: 'Search Error',
      detail: 'Failed to search inventory items',
      life: 3000
    });
  } finally {
    isSearchingItems.value = false;
  }
};

// Load item locations when an item is selected
const loadItemLocations = async (item: InventoryItemSearchResult | null) => {
  if (!item || !item.id) {
    itemLocations.value = [];
    selectedLocation.value = null;
    return;
  }

  try {
    isLoadingLocations.value = true;
    const locations = await inventoryService.getItemLocationsByItemId(item.id, true) as InventoryItemLocationWithDetails[];
    // Filter to only show locations with inventory and sort by quantity (highest first)
    itemLocations.value = locations
      .filter(loc => loc.quantity > 0)
      .sort((a, b) => b.quantity - a.quantity);
    selectedLocation.value = null;
  } catch (error) {
    logger.error('Error loading item locations:', error);
    itemLocations.value = [];
    toast.add({
      severity: 'error',
      summary: 'Location Error',
      detail: 'Failed to load item locations',
      life: 3000
    });
  } finally {
    isLoadingLocations.value = false;
  }
};

// Watch for changes in selectedItem
watch(selectedItem, (newItem) => {
  void loadItemLocations(newItem);
});

// Load all locations for general reference
const loadLocations = async () => {
  try {
    const locations = await inventoryService.getAllLocations();
    availableLocations.value = locations;
  } catch (error) {
    logger.error('Error loading locations:', error);
  }
};

const addInventoryItem = () => {
  if (!selectedItem.value || !itemQuantity.value || itemQuantity.value <= 0 || !selectedLocation.value) {
    toast.add({
      severity: 'error',
      summary: 'Invalid Input',
      detail: 'Please select an item, location, and specify a valid quantity',
      life: 3000
    });
    return;
  }

  // Validate that the selected item is a valid inventory item (has an id property)
  if (!selectedItem.value.id) {
    toast.add({
      severity: 'error',
      summary: 'Invalid Item',
      detail: 'Please select a valid item from the suggestions list.',
      life: 3000
    });
    return;
  }

  // Check if sufficient inventory is available at selected location
  const availableAtLocation = selectedLocation.value.quantity || 0;
  if (availableAtLocation < itemQuantity.value) {
    toast.add({
      severity: 'error',
      summary: 'Insufficient Inventory',
      detail: `Only ${availableAtLocation} ${selectedItem.value.unit_of_measure} available at ${selectedLocation.value.location?.name || 'selected location'}`,
      life: 3000
    });
    return;
  }

  const newItem: ClipOnUsagePart = {
    item_id: selectedItem.value.id,
    item_name: selectedItem.value.name,
    part_number: selectedItem.value.part_number || undefined,
    sku: selectedItem.value.sku || undefined,
    quantity: itemQuantity.value,
    unit_of_measure: selectedItem.value.unit_of_measure,
    notes: itemNotes.value,
    equipmentType: selectedEquipmentType.value,
    equipmentLabel: equipmentTypeOptions.value.find(opt => opt.value === selectedEquipmentType.value)?.label,
    location_id: selectedLocation.value.location_id,
    location_name: selectedLocation.value.location?.name || 'Unknown Location'
  };

  inventoryItems.value.push(newItem);

  // Reset form
  selectedItem.value = null;
  itemQuantity.value = 1;
  itemNotes.value = '';
  selectedEquipmentType.value = 'clip';
  selectedLocation.value = null;
  itemLocations.value = [];
  inventoryDialog.value = false;

  toast.add({
    severity: 'success',
    summary: 'Item Added',
    detail: `${newItem.item_name} added to parts list`,
    life: 2000
  });
};

const removeInventoryItem = (index: number) => {
  inventoryItems.value.splice(index, 1);
};

const openInventoryDialog = async () => {
  await loadLocations();
  inventoryDialog.value = true;
};

// Computed helper for template access
const getEquipmentTypeOption = (value: string) => {
  return equipmentTypeOptions.value.find(opt => opt.value === value);
};

</script>

<template>
  <Dialog
    v-model:visible="installClipDialogVisible"
    append-to="body"
    modal
    :breakpoints="{'960px': '75vw', '640px': '100vw'}"
    :style="{width: '30vw'}"
    :draggable="false"
    :resizable="false"
  >
    <template #header>
      <h1 class="m-0 text-surface-900 dark:text-surface-0 font-semibold text-xl leading-normal">
        Install {{ clipNumber }}
      </h1>
    </template>
    <form class="flex flex-col gap-4 mt-4">
      <div>
        <label
          for="container"
          class="block mb-1 text-color text-base"
        >Container Number</label>
        <InputGroup>
          <InputGroupAddon>
            <i class="pi pi-hashtag" />
          </InputGroupAddon>
          <InputText
            id="container"
            v-model="installFormObject.container"
            name="container"
            type="text"
            class="w-full"
            placeholder="Container Number"
          />
        </InputGroup>
      </div>
      <div class="flex gap-4">
        <div class="w-full">
          <label
            for="fuel-level"
            class="block mb-1 text-color text-base"
          >Fuel Level</label>
          <InputGroup>
            <InputGroupAddon>
              <i class="pi pi-gauge text-lg" />
            </InputGroupAddon>
            <Dropdown
              id="fuel-level"
              v-model="installFormObject.fuelLevel"
              :options="fuelOptions.toReversed()"
              input-id="fuel-level"
            />
          </InputGroup>
        </div>
        <div class="w-full">
          <label
            for="setpoint"
            class="block mb-1 text-color text-base"
          >Setpoint</label>
          <InputGroup>
            <InputGroupAddon>
              <i class="fa-solid fa-temperature-three-quarters" />
            </InputGroupAddon>
            <InputText
              id="setpoint"
              v-model="installFormObject.setpoint"
              name="setpoint"
              type="text"
              class="w-full"
              placeholder="Setpoint"
            />
          </InputGroup>
        </div>
      </div>
      <div class="flex gap-4">
        <div class="w-full">
          <label
            for="return"
            class="block mb-1 text-color text-base"
          >Return</label>
          <InputGroup>
            <InputGroupAddon>
              <i class="fa-solid fa-temperature-three-quarters" />
            </InputGroupAddon>
            <InputText
              id="return"
              v-model="installFormObject.return"
              name="return"
              type="text"
              class="w-full"
              placeholder="Return"
            />
          </InputGroup>
        </div>
        <div class="w-full">
          <label
            for="supply"
            class="block mb-1 text-color text-base"
          >Supply</label>
          <InputGroup>
            <InputGroupAddon>
              <i class="fa-solid fa-temperature-three-quarters" />
            </InputGroupAddon>
            <InputText
              id="supply"
              v-model="installFormObject.supply"
              name="supply"
              type="text"
              class="w-full"
              placeholder="Supply"
            />
          </InputGroup>
        </div>
      </div>

      <!-- Forklift section - reorganized for better responsive layout -->
      <div class="flex gap-4">
        <div class="w-full">
          <label
            for="forklift-propane"
            class="block mb-1 text-color text-base"
          >Forklift Propane</label>
          <InputGroup>
            <InputGroupAddon>
              <i class="pi pi-gauge text-lg" />
            </InputGroupAddon>
            <Dropdown
              id="forklift-propane"
              v-model="installFormObject.forkliftPropane"
              input-id="forklift-propane"
              :options="fuelOptions"
            />
          </InputGroup>
        </div>
        <div class="w-full">
          <label
            for="forklift-gas"
            class="block mb-1 text-color text-base"
          >Forklift Gas</label>
          <InputGroup>
            <InputGroupAddon>
              <i class="pi pi-gauge text-lg" />
            </InputGroupAddon>
            <Dropdown
              id="forklift-gas"
              v-model="installFormObject.forkliftGas"
              input-id="forklift-gas"
              :options="fuelOptions"
            />
          </InputGroup>
        </div>
      </div>

      <!-- Forklift Hours in its own row -->
      <div class="flex gap-4">
        <div class="w-full md:w-1/2">
          <label
            for="forklift-hours"
            class="block mb-1 text-color text-base"
          >Forklift Hours</label>
          <InputGroup>
            <InputGroupAddon>
              <i class="pi pi-clock" />
            </InputGroupAddon>
            <InputText
              id="forklift-hours"
              v-model="installFormObject.forkliftHours"
              name="forklift-hours"
              type="text"
              class="w-full"
              placeholder="Hours"
            />
          </InputGroup>
        </div>
      </div>

      <!-- Parts Used Section -->
      <div class="flex flex-col gap-3">
        <div class="flex items-center justify-between">
          <h3 class="text-lg font-semibold text-color">
            Parts Used
          </h3>
          <Button 
            label="Add Part" 
            icon="pi pi-plus" 
            size="small"
            class="p-button-text"
            @click="openInventoryDialog"
          />
        </div>
        
        <!-- Parts list -->
        <div
          v-if="inventoryItems.length > 0"
          class="space-y-2"
        >
          <div 
            v-for="(item, index) in inventoryItems" 
            :key="index"
            class="flex items-center justify-between p-3 border border-surface-border rounded-lg bg-surface-50 dark:bg-surface-800"
          >
            <div class="flex-1">
              <div class="flex items-center gap-2">
                <Badge 
                  :value="item.equipmentLabel" 
                  :severity="item.equipmentType === 'clip' ? 'info' : 'success'"
                  class="text-xs"
                />
                <span class="font-medium">{{ item.item_name }}</span>
              </div>
              <div class="text-sm text-gray-600 dark:text-gray-300">
                <span v-if="item.part_number">Part #: {{ item.part_number }} • </span>
                <span v-if="item.sku">SKU: {{ item.sku }} • </span>
                Qty: {{ item.quantity }} {{ item.unit_of_measure }}
                <span v-if="item.location_name"> from {{ item.location_name }}</span>
              </div>
              <div
                v-if="item.notes"
                class="text-sm text-gray-500 italic"
              >
                {{ item.notes }}
              </div>
            </div>
            <Button 
              v-tooltip="'Remove item'" 
              icon="pi pi-times"
              class="p-button-text p-button-danger p-button-sm"
              @click="removeInventoryItem(index)"
            />
          </div>
        </div>
        
        <div
          v-else
          class="text-center py-4 text-gray-500 italic"
        >
          No parts added yet
        </div>
      </div>

      <div class="flex gap-4">
        <div class="w-full">
          <label
            for="notes"
            class="block mb-1 text-color text-base"
          >Notes</label>
          <Textarea
            id="notes"
            v-model="installFormObject.notes"
            class="w-full"
            name="notes"
          />
        </div>
      </div>
    </form>
    <template #footer>
      <div class="flex gap-4 justify-end border-t border-surface pt-8">
        <Button
          label="Cancel"
          class="p-button-text"
          @click="installClipDialogVisible = false"
        />
        <Button
          label="Install"
          :loading="isSubmitting"
          :disabled="isSubmitting"
          class="p-button-rounded"
          @click="submitClipInstall"
        />
      </div>
    </template>
  </Dialog>

  <!-- Inventory Selection Dialog -->
  <Dialog 
    v-model:visible="inventoryDialog" 
    header="Add Parts/Components" 
    :style="{ width: '500px' }" 
    modal 
    class="p-fluid"
  >
    <div class="field">
      <label for="equipment-type">Used On*</label>
      <Dropdown 
        id="equipment-type"
        v-model="selectedEquipmentType" 
        :options="equipmentTypeOptions"
        option-label="label"
        option-value="value"
        placeholder="Select equipment"
      >
        <template #option="slotProps">
          <div class="flex items-center gap-2">
            <i :class="slotProps.option.icon" />
            <span>{{ slotProps.option.label }}</span>
          </div>
        </template>
        <template #value="slotProps">
          <div
            v-if="slotProps.value"
            class="flex items-center gap-2"
          >
            <i :class="getEquipmentTypeOption(slotProps.value)?.icon" />
            <span>{{ getEquipmentTypeOption(slotProps.value)?.label }}</span>
          </div>
          <span v-else>{{ slotProps.placeholder }}</span>
        </template>
      </Dropdown>
      <small class="text-gray-500 mt-1">Specify which equipment this part will be used on</small>
    </div>

    <div class="field">
      <label for="item">Item*</label>
      <AutoComplete 
        id="item" 
        v-model="selectedItem" 
        :suggestions="itemSuggestions"
        option-label="name" 
        placeholder="Type to search items..."
        :loading="isSearchingItems"
        :min-length="2"
        class="w-full"
        @complete="searchInventoryItems"
      >
        <template #option="slotProps">
          <div class="flex flex-col">
            <span>{{ slotProps.option.name }}</span>
            <small
              v-if="slotProps.option.sku"
              class="text-gray-500"
            >SKU: {{ slotProps.option.sku }}</small>
            <small
              v-if="slotProps.option.part_number"
              class="text-gray-500"
            >Part #: {{ slotProps.option.part_number }}</small>
            <small class="text-gray-500">Unit: {{ slotProps.option.unit_of_measure }}</small>
            <small
              v-if="slotProps.option.current_quantity !== undefined"
              class="text-blue-600 font-medium"
            >
              Available: {{ slotProps.option.current_quantity }} {{ slotProps.option.unit_of_measure }}
            </small>
          </div>
        </template>
      </AutoComplete>
      <small class="text-gray-500 mt-1">Type at least 2 characters to search</small>
    </div>

    <div
      v-if="selectedItem"
      class="field"
    >
      <label for="location">Location*</label>
      <Dropdown 
        id="location"
        v-model="selectedLocation" 
        :options="itemLocations"
        option-label="location.name"
        placeholder="Select location"
        :loading="isLoadingLocations"
        class="w-full"
      >
        <template #option="slotProps">
          <div class="flex justify-between items-center w-full">
            <span>{{ slotProps.option.location?.name || 'Unknown Location' }}</span>
            <Badge 
              :value="`${slotProps.option.quantity} ${selectedItem.unit_of_measure}`" 
              :severity="slotProps.option.quantity > 0 ? 'success' : 'danger'"
              class="text-xs"
            />
          </div>
        </template>
        <template #value="slotProps">
          <div
            v-if="slotProps.value"
            class="flex justify-between items-center w-full"
          >
            <span>{{ slotProps.value.location?.name || 'Unknown Location' }}</span>
            <Badge 
              :value="`${slotProps.value.quantity} ${selectedItem.unit_of_measure}`" 
              :severity="slotProps.value.quantity > 0 ? 'success' : 'danger'"
              class="text-xs"
            />
          </div>
          <span v-else>{{ slotProps.placeholder }}</span>
        </template>
      </Dropdown>
      <small class="text-gray-500 mt-1">Select the location to take inventory from</small>
    </div>

    <div class="field">
      <label for="quantity">Quantity*</label>
      <InputNumber 
        id="quantity" 
        v-model="itemQuantity" 
        :min="1" 
        :max="selectedLocation?.quantity || selectedItem?.current_quantity || undefined"
        :show-buttons="false"
      />
      <div
        v-if="selectedItem"
        class="mt-1"
      >
        <small
          v-if="selectedLocation"
          class="text-green-600 font-medium"
        >
          Available at {{ selectedLocation.location?.name }}: {{ selectedLocation.quantity }} {{ selectedItem.unit_of_measure }}
        </small>
        <small
          v-else-if="selectedItem.current_quantity && selectedItem.current_quantity > 0"
          class="text-blue-600 font-medium"
        >
          Total Available: {{ selectedItem.current_quantity }} {{ selectedItem.unit_of_measure }} (select location)
        </small>
        <small
          v-else
          class="text-red-600 font-medium"
        >
          No inventory available
        </small>
      </div>
    </div>

    <div class="field">
      <label for="item-notes">Notes</label>
      <Textarea
        id="item-notes"
        v-model="itemNotes"
        rows="3"
      />
    </div>

    <template #footer>
      <Button 
        label="Cancel" 
        icon="pi pi-times" 
        class="p-button-text" 
        @click="inventoryDialog = false"
      />
      <Button 
        label="Add" 
        icon="pi pi-check" 
        class="p-button-text" 
        :disabled="!selectedItem || !selectedLocation || !itemQuantity || itemQuantity <= 0 || (selectedLocation?.quantity || 0) <= 0"
        @click="addInventoryItem"
      />
    </template>
  </Dialog>
</template>

<style scoped>

</style>
